import React, { useCallback, useEffect, useRef, useState } from 'react';
import MaterialTable from 'material-table';
import {
    Alert, AppBar, Autocomplete, Avatar, Box, Button, Card, CardContent, Checkbox, Chip,
    CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, IconButton, InputLabel, MenuItem, Pagination, Select, Skeleton, Snackbar, Stack,
    Tab, Tabs, TextField, Toolbar, Typography, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
    exportAdminOrders, getAdminOrderDetail, getAdminOrderList,
    getAdminOrderOptions, getFulfillmentQueue, searchAdminOrders,
    updateAdminOrderShipping, updateAdminOrdersShippingBulk
} from '../../../actions/orders';
import { adminOrder } from '../../../constants/constants';
import './Pedidos.css';
import InfoPedido from './InfoPedido';
import InfoCliente from './InfoCliente';
import TablaPedidos from './TablaPedidos';
import InfoFacturacion from './InfoFacturacion';
import EstadoEnvioMelonn from './EstadoEnvioMelonn';
import EnvioAlmacen from './EnvioAlmacen/EnvioAlmacen';
import {
    elapsedLabel, formatAdminOrderDate, formatAdminOrderTableDate,
    formatAdminOrderTotal, formatAdminDateKey, formatAdminDateRange,
    orderDatePreset, transitionNotice
} from '../../../helpers/admin/orderManagementHelper';

const PAGE_SIZE = 10;
const QUEUE_PAGE_SIZE = 10;
const DEFAULT_QUEUE_PERIOD = { period: '30days', dateFrom: '', dateTo: '' };
const QUEUE_PERIOD_LABELS = {
    '30days': 'Últimos 30 días', '90days': 'Últimos 90 días',
    custom: 'Rango personalizado', all: 'Todos los históricos'
};
const emptyOptions = { dateFrom: '', dateTo: '', partners: [], paymentMethods: [], shippingStatuses: [] };
const emptyFilters = dates => ({
    preset: 'current', dateFrom: dates.dateFrom || '', dateTo: dates.dateTo || '',
    partner: '', invoice: 'all', paymentMethod: '', shippingStatus: '', odooStatus: 'all'
});

const Queue = ({ partners, refreshKey, onTransition, onBulkTransition, onOpenDetail }) => {
    const [tab, setTab] = useState('preparing');
    const [pages, setPages] = useState({ preparing: 1, shipped: 1 });
    const [data, setData] = useState({ preparing: null, shipped: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState([]);
    const [confirmBulk, setConfirmBulk] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [periodDraft, setPeriodDraft] = useState({
        preparing: { ...DEFAULT_QUEUE_PERIOD }, shipped: { ...DEFAULT_QUEUE_PERIOD }
    });
    const [periodApplied, setPeriodApplied] = useState({
        preparing: { ...DEFAULT_QUEUE_PERIOD }, shipped: { ...DEFAULT_QUEUE_PERIOD }
    });
    const compact = useMediaQuery('(max-width:900px)');

    const load = useCallback(async status => {
        setLoading(true);
        setError('');
        try {
            const result = await getFulfillmentQueue(
                status, pages[status], QUEUE_PAGE_SIZE, periodApplied[status]
            );
            setData(current => ({ ...current, [status]: result }));
        } catch (requestError) {
            setError('No fue posible cargar la bandeja operativa.');
        } finally {
            setLoading(false);
        }
    }, [pages, periodApplied]);

    useEffect(() => { load('preparing'); load('shipped'); }, [load, refreshKey]);
    useEffect(() => {
        setSelected([]);
        setConfirmBulk(false);
    }, [tab, pages.shipped, periodApplied, refreshKey]);
    const current = data[tab] || { items: [], total: 0, totalPages: 1, page: 1 };
    const eligibleVisible = tab === 'shipped'
        ? current.items.filter(order => order.delivery_eligible)
        : [];
    const allVisibleSelected = eligibleVisible.length > 0 && eligibleVisible.every(
        order => selected.includes(order.id)
    );
    const selectedOrders = current.items.filter(order => selected.includes(order.id));

    const toggleSelection = id => setSelected(currentSelection => currentSelection.includes(id)
        ? currentSelection.filter(value => value !== id)
        : [...currentSelection, id]);

    const applyPeriodFilter = () => {
        setPages(currentPages => ({ ...currentPages, [tab]: 1 }));
        setPeriodApplied(currentFilters => ({
            ...currentFilters, [tab]: { ...periodDraft[tab] }
        }));
    };

    const deliverSelected = async () => {
        setBulkLoading(true);
        setError('');
        try {
            const result = await updateAdminOrdersShippingBulk(selected);
            setConfirmBulk(false);
            setSelected([]);
            await onBulkTransition(selected, result);
        } catch (requestError) {
            setError('No fue posible actualizar los pedidos seleccionados.');
        } finally {
            setBulkLoading(false);
        }
    };

    return (
        <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Box>
                        <Typography variant="h6">Bandeja operativa</Typography>
                        <Typography variant="body2" color="text.secondary">Actualiza envíos sin abrir cada pedido.</Typography>
                    </Box>
                    <IconButton onClick={() => load(tab)} aria-label="Recargar bandeja"><RefreshIcon /></IconButton>
                </Stack>
                <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
                    <Tab value="preparing" label={`Por enviar (${data.preparing?.total ?? '…'})`} />
                    <Tab value="shipped" label={`En tránsito (${data.shipped?.total ?? '…'})`} />
                </Tabs>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {(
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f7f9fa', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small"><InputLabel>
                                    {tab === 'shipped' ? 'Periodo de envío' : 'Periodo del pedido'}
                                </InputLabel>
                                    <Select value={periodDraft[tab].period}
                                        label={tab === 'shipped' ? 'Periodo de envío' : 'Periodo del pedido'}
                                        onChange={event => setPeriodDraft(currentFilters => ({
                                            ...currentFilters,
                                            [tab]: { ...currentFilters[tab], period: event.target.value }
                                        }))}>
                                        <MenuItem value="30days">Últimos 30 días</MenuItem>
                                        <MenuItem value="90days">Últimos 90 días</MenuItem>
                                        <MenuItem value="custom">Rango personalizado</MenuItem>
                                        <MenuItem value="all">Todos los históricos</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {periodDraft[tab].period === 'custom' && <>
                                <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date"
                                    label="Desde" InputLabelProps={{ shrink: true }} value={periodDraft[tab].dateFrom}
                                    onChange={event => setPeriodDraft(currentFilters => ({
                                        ...currentFilters,
                                        [tab]: { ...currentFilters[tab], dateFrom: event.target.value }
                                    }))} /></Grid>
                                <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date"
                                    label="Hasta" InputLabelProps={{ shrink: true }} value={periodDraft[tab].dateTo}
                                    onChange={event => setPeriodDraft(currentFilters => ({
                                        ...currentFilters,
                                        [tab]: { ...currentFilters[tab], dateTo: event.target.value }
                                    }))} /></Grid>
                            </>}
                            <Grid item xs={12} sm="auto"><Button variant="outlined" onClick={applyPeriodFilter}
                                disabled={periodDraft[tab].period === 'custom' &&
                                    (!periodDraft[tab].dateFrom || !periodDraft[tab].dateTo)}>
                                Aplicar periodo
                            </Button></Grid>
                        </Grid>
                        <Box sx={{ mt: 1.5 }}>
                            {current.period ? <>
                                <Typography variant="subtitle2">
                                    Periodo aplicado: {QUEUE_PERIOD_LABELS[current.period.name]}
                                </Typography>
                                <Typography variant="body2">
                                    {current.period.name === 'all'
                                        ? <><b>{current.total ?? 0}</b> pedidos</>
                                        : <>{formatAdminDateRange(current.period.dateFrom, current.period.dateTo)} · <b>{current.total ?? 0}</b> pedidos</>}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {current.eligibleTotal ?? 0} dentro de la ventana operativa · {current.historicalTotal ?? 0} históricos de sólo consulta
                                </Typography>
                            </> : <Skeleton width={360} height={48} />}
                        </Box>
                    </Box>
                )}
                {loading && !data[tab] ? <Skeleton variant="rectangular" height={150} /> : (
                    <Stack spacing={1.5}>
                        {current.items.length === 0 && <Alert severity="info">No hay pedidos en esta bandeja.</Alert>}
                        {tab === 'shipped' && current.items.length > 0 && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                                alignItems={{ sm: 'center' }} justifyContent="space-between">
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Button size="small" disabled={!eligibleVisible.length}
                                        onClick={() => setSelected(allVisibleSelected
                                        ? []
                                        : eligibleVisible.map(order => order.id))}>
                                        {allVisibleSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                    </Button>
                                    <Typography variant="body2">{selected.length} seleccionados</Typography>
                                </Stack>
                                <Button variant="contained" color="success" disabled={!selected.length}
                                    onClick={() => setConfirmBulk(true)}>
                                    Marcar seleccionados como entregados
                                </Button>
                            </Stack>
                        )}
                        {tab === 'preparing' ? (
                            <Grid container spacing={2}>
                                {current.items.map(order => (
                                    <Grid item xs={12} md={6} key={order.id}>
                                        <Card variant="outlined" sx={{ height: '100%',
                                            borderLeft: order.is_btl ? '5px solid #7E57C2' : undefined,
                                            bgcolor: order.odoo_registered ? '#f1fbf5' : 'background.paper' }}>
                                            <CardContent>
                                                <Box sx={{ mb: 2 }}>
                                            <Button variant="text" onClick={() => onOpenDetail(order.id)} sx={{ p: 0 }}>
                                                PEDIDO {order.order_num}{order.odoo_registered
                                                    ? ` (Odoo ${order.odoo_order_name})` : ''}
                                            </Button>
                                            <Typography variant="body2">{order.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                        {elapsedLabel(order.order_date)}
                                            </Typography>
                                                </Box>
                                                <EnvioAlmacen selectedOrder={{ id: order.id, order_num: order.order_num,
                                                    shipping: { status: 'preparing', partner: order.partner,
                                                        tracking_guide: order.tracking_guide },
                                                    shipping_eligible: order.shipping_eligible }}
                                                    partnerOptions={partners} onTransition={onTransition} />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : current.items.map(order => (
                            <Card key={order.id} variant="outlined" sx={{
                                borderLeft: order.is_btl ? '5px solid #7E57C2' : undefined,
                                bgcolor: order.odoo_registered ? '#f1fbf5' : 'background.paper'
                            }}>
                                <CardContent sx={{ py: '12px !important' }}>
                                    <Stack direction={compact ? 'column' : 'row'} spacing={1.5} alignItems={compact ? 'flex-start' : 'center'}>
                                        <Checkbox checked={selected.includes(order.id)} disabled={!order.delivery_eligible}
                                            onChange={() => toggleSelection(order.id)}
                                            inputProps={{ 'aria-label': `Seleccionar pedido ${order.order_num}` }} />
                                        <Box sx={{ minWidth: 190 }}>
                                            <Button variant="text" onClick={() => onOpenDetail(order.id)} sx={{ p: 0 }}>
                                                PEDIDO {order.order_num}{order.odoo_registered
                                                    ? ` (Odoo ${order.odoo_order_name})` : ''}
                                            </Button>
                                            <Typography variant="body2">{order.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {elapsedLabel(order.dispatch_date)}
                                            </Typography>
                                        </Box>
                                                <Stack direction={compact ? 'column' : 'row'} spacing={2}
                                                    alignItems={compact ? 'flex-start' : 'center'} sx={{ flexGrow: 1 }}>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="body2"><b>{order.partner || 'Sin paquetería'}</b> · {order.tracking_guide || 'Sin guía'}</Typography>
                                                        <Typography variant="caption">Enviado: {formatAdminOrderDate(order.dispatch_date)}</Typography>
                                                    </Box>
                                                    <EnvioAlmacen selectedOrder={{ id: order.id, order_num: order.order_num,
                                                        shipping: { status: 'shipped', partner: order.partner,
                                                            tracking_guide: order.tracking_guide },
                                                        delivery_eligible: order.delivery_eligible }}
                                                        partnerOptions={partners} onTransition={onTransition} />
                                                </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                        {current.totalPages > 1 && <Pagination count={current.totalPages} page={pages[tab]}
                            onChange={(_event, page) => setPages(value => ({ ...value, [tab]: page }))} />}
                    </Stack>
                )}
            </CardContent>
            <Dialog open={confirmBulk} onClose={() => !bulkLoading && setConfirmBulk(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirmar entrega de {selectedOrders.length} pedidos</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Se enviará la confirmación de entrega y la encuesta correspondiente a cada pedido actualizado.
                    </Typography>
                    <Stack spacing={1}>
                        {selectedOrders.map(order => (
                            <Box key={order.id} sx={{ p: 1, bgcolor: '#f7f9fa', borderRadius: 1 }}>
                                <Typography variant="body2"><b>{order.order_num}</b> · {order.odoo_order_name}</Typography>
                                <Typography variant="caption">{order.name}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={bulkLoading} onClick={() => setConfirmBulk(false)}>Cancelar</Button>
                    <Button variant="contained" color="success" disabled={bulkLoading}
                        onClick={deliverSelected}>
                        {bulkLoading ? 'Actualizando…' : 'Confirmar entregas'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

const Filters = ({ options, draft, setDraft, applied, onApply, onClear, onExport, exporting }) => {
    const changePreset = preset => {
        if (preset === 'custom') setDraft(current => ({ ...current, preset }));
        else setDraft(current => ({ ...current, preset, ...orderDatePreset(preset, options.dateTo) }));
    };
    return (
        <Card variant="outlined" sx={{ mt: 3 }}><CardContent>
            <Typography variant="h6" gutterBottom>Filtros de pedidos</Typography>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Periodo</InputLabel>
                    <Select value={draft.preset} label="Periodo" onChange={event => changePreset(event.target.value)}>
                        <MenuItem value="current">Este mes</MenuItem><MenuItem value="previous">Mes anterior</MenuItem>
                        <MenuItem value="90days">Últimos 90 días</MenuItem><MenuItem value="custom">Personalizado</MenuItem>
                    </Select></FormControl></Grid>
                <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date" label="Desde"
                    InputLabelProps={{ shrink: true }} value={draft.dateFrom}
                    onChange={event => setDraft(current => ({ ...current, preset: 'custom', dateFrom: event.target.value }))} /></Grid>
                <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date" label="Hasta"
                    InputLabelProps={{ shrink: true }} value={draft.dateTo}
                    onChange={event => setDraft(current => ({ ...current, preset: 'custom', dateTo: event.target.value }))} /></Grid>
                <Grid item xs={12} sm={6} md={2}><FormControl fullWidth size="small"><InputLabel>Paquetería</InputLabel>
                    <Select value={draft.partner} label="Paquetería" onChange={event => setDraft(current => ({ ...current, partner: event.target.value }))}>
                        <MenuItem value="">Todas</MenuItem>{options.partners.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={2}><FormControl fullWidth size="small"><InputLabel>Factura</InputLabel>
                    <Select value={draft.invoice} label="Factura" onChange={event => setDraft(current => ({ ...current, invoice: event.target.value }))}>
                        <MenuItem value="all">Todas</MenuItem><MenuItem value="yes">Requiere factura</MenuItem><MenuItem value="no">Sin factura</MenuItem>
                    </Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Método de pago</InputLabel>
                    <Select value={draft.paymentMethod} label="Método de pago" onChange={event => setDraft(current => ({ ...current, paymentMethod: event.target.value }))}>
                        <MenuItem value="">Todos</MenuItem>{options.paymentMethods.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Estado de envío</InputLabel>
                    <Select value={draft.shippingStatus} label="Estado de envío" onChange={event => setDraft(current => ({ ...current, shippingStatus: event.target.value }))}>
                        <MenuItem value="">Todos</MenuItem>{options.shippingStatuses.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                    </Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth size="small"><InputLabel>Registro en Odoo</InputLabel>
                    <Select value={draft.odooStatus} label="Registro en Odoo" onChange={event => setDraft(current => ({ ...current, odooStatus: event.target.value }))}>
                        <MenuItem value="all">Todos</MenuItem><MenuItem value="registered">Registrados</MenuItem><MenuItem value="pending">No registrados</MenuItem>
                    </Select></FormControl></Grid>
                <Grid item xs={12} md={3}><Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button variant="contained" onClick={onApply}>Aplicar</Button>
                    <Button onClick={onClear}>Limpiar</Button>
                    <Button startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                        disabled={exporting} onClick={onExport}>CSV</Button>
                </Stack></Grid>
            </Grid>
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#eef7fb', borderRadius: 1 }}>
                <Typography variant="subtitle2">Filtros aplicados</Typography>
                <Stack direction="row" flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
                    <Chip size="small" label={formatAdminDateRange(applied.dateFrom, applied.dateTo)} />
                    {applied.partner && <Chip size="small" label={`Paquetería: ${applied.partner}`} />}
                    {applied.invoice !== 'all' && <Chip size="small" label={applied.invoice === 'yes' ? 'Requiere factura' : 'Sin factura'} />}
                    {applied.paymentMethod && <Chip size="small" label={`Pago: ${applied.paymentMethod}`} />}
                    {applied.shippingStatus && <Chip size="small" label={`Envío: ${applied.shippingStatus}`} />}
                    {applied.odooStatus !== 'all' && <Chip size="small" label={applied.odooStatus === 'registered' ? 'Registrado en Odoo' : 'No registrado en Odoo'} />}
                </Stack>
            </Box>
        </CardContent></Card>
    );
};

const ExpiredShippingManagement = ({ order }) => {
    const reason = order.shipping_eligibility_reason;
    const deadline = formatAdminDateKey(order.shipping_operational_deadline);
    const explanation = reason === 'shipping_window_expired'
        ? `El periodo operativo finalizó el ${deadline}. Este pedido ya no puede marcarse como enviado desde ONL.`
        : reason === 'missing_order_date'
            ? 'El pedido no tiene una fecha válida para determinar su periodo operativo.'
            : 'La fecha del pedido no pertenece al periodo operativo vigente.';
    return (
        <Card variant="outlined" sx={{ borderColor: 'warning.light' }}>
            <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>Gestión de envío</Typography>
                    <Chip size="small" color="warning" label="Sólo consulta" />
                </Stack>
                <Typography variant="body2">{explanation}</Typography>
                <Typography variant="caption" color="text.secondary">
                    Disponible únicamente para consulta.
                </Typography>
            </CardContent>
        </Card>
    );
};

const DetailDialog = ({ open, loading, error, order, partners, onClose, onTransition }) => (
    <Dialog fullScreen open={open} onClose={onClose}>
        <AppBar sx={{ position: 'relative', backgroundColor: '#04A6E1' }}><Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose}><CloseIcon /></IconButton>
        </Toolbar></AppBar>
        <Container maxWidth="xxl" sx={{ my: 5 }}>
            {loading && <Stack spacing={2}><Skeleton width="30%" height={50} /><Skeleton variant="rectangular" height={300} /></Stack>}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && order && <>
                <Typography variant="h5">Pedido: {order.order_num}</Typography>
                <Typography variant="button"># Pedido Odoo: {order.odoo_order_name}</Typography>
                <Typography variant="subtitle1">Fecha del pedido: {order.full_order_date}</Typography>
                <Typography variant="subtitle1">Pagado con: <b style={{ color: '#3498DB' }}>{order.payment?.method}</b></Typography>
                <Box sx={{ mt: 5 }}><TablaPedidos selectedOrder={order} /></Box>
                <Grid container spacing={4} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}><Card><CardContent><Typography variant="h5" fontWeight={600}>Detalle de facturación</Typography><InfoFacturacion selectedOrder={order} /></CardContent></Card></Grid>
                    <Grid item xs={12} md={6}><Card><CardContent><Typography variant="h5" fontWeight={600}>Estado del envío</Typography><EstadoEnvioMelonn selectedOrder={order} /></CardContent></Card></Grid>
                    <Grid item xs={12} md={6}><Card><CardContent><Typography variant="h5" fontWeight={600}>Información del cliente</Typography><InfoCliente selectedOrder={order} /></CardContent></Card></Grid>
                    <Grid item xs={12} md={6}><Card><CardContent><Typography variant="h5" fontWeight={600}>Información del pedido</Typography><InfoPedido selectedOrder={order} /></CardContent></Card></Grid>
                    <Grid item xs={12} md={6}>
                        {order.shipping?.status === 'preparing' && order.shipping_eligible === false
                            ? <ExpiredShippingManagement order={order} />
                            : <Card><CardContent><Typography variant="h5" fontWeight={600}>Gestión de envío</Typography>
                                <EnvioAlmacen selectedOrder={order} partnerOptions={partners} onTransition={onTransition} />
                            </CardContent></Card>}
                    </Grid>
                </Grid>
            </>}
        </Container>
    </Dialog>
);

const AdminPedidos = () => {
    const [options, setOptions] = useState(emptyOptions);
    const [draft, setDraft] = useState(emptyFilters(emptyOptions));
    const [applied, setApplied] = useState(emptyFilters(emptyOptions));
    const [list, setList] = useState({ items: [], total: 0, page: 1, totalPages: 1 });
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState('');
    const [queueRefresh, setQueueRefresh] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const searchSequence = useRef(0);
    const detailCache = useRef(new Map());
    const [dialog, setDialog] = useState({ open: false, loading: false, error: '', order: null });
    const [notice, setNotice] = useState('');
    const [exporting, setExporting] = useState(false);

    const loadList = useCallback(async (filters, page = 1) => {
        setLoading(true); setListError('');
        try {
            const result = await getAdminOrderList({ ...filters, page, pageSize: PAGE_SIZE });
            setList(result);
        } catch (error) {
            setListError('No fue posible cargar los pedidos.');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        let active = true;
        getAdminOrderOptions().then(result => {
            if (!active) return;
            const initial = emptyFilters(result);
            setOptions(result); setDraft(initial); setApplied(initial); loadList(initial, 1);
        }).catch(() => { if (active) {
            setListError('No fue posible cargar las opciones.');
            setLoading(false);
        } });
        return () => { active = false; };
    }, [loadList]);

    useEffect(() => {
        const query = searchText.trim();
        if (query.length < 2) { setSearchOptions([]); setSearching(false); return undefined; }
        const sequence = ++searchSequence.current;
        setSearching(true);
        const timer = setTimeout(() => searchAdminOrders(query).then(result => {
            if (sequence === searchSequence.current) setSearchOptions(result.items);
        }).catch(() => {
            if (sequence === searchSequence.current) setSearchOptions([]);
        }).finally(() => {
            if (sequence === searchSequence.current) setSearching(false);
        }), 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    const openDetail = useCallback(async id => {
        const cached = detailCache.current.get(id);
        setDialog({ open: true, loading: !cached, error: '', order: cached || null });
        if (cached) return;
        try {
            const result = await getAdminOrderDetail(id);
            detailCache.current.set(id, result.order);
            setDialog({ open: true, loading: false, error: '', order: result.order });
        } catch (error) {
            setDialog(current => ({ ...current, loading: false, error: 'No fue posible cargar el detalle.' }));
        }
    }, []);

    const transition = useCallback(async (id, payload) => {
        const result = await updateAdminOrderShipping(id, payload);
        if (result.transition === 'changed') {
            detailCache.current.delete(id);
            if (dialog.order?.id === id) {
                const detail = await getAdminOrderDetail(id);
                detailCache.current.set(id, detail.order);
                setDialog(current => ({ ...current, order: detail.order }));
            }
            setQueueRefresh(value => value + 1);
            loadList(applied, list.page);
        }
        const message = transitionNotice(result);
        if (message) setNotice(message);
        return result;
    }, [applied, dialog.order, list.page, loadList]);

    const bulkTransition = useCallback(async (ids, result) => {
        ids.forEach(id => detailCache.current.delete(id));
        if (dialog.order?.id && ids.includes(dialog.order.id)) {
            try {
                const detail = await getAdminOrderDetail(dialog.order.id);
                detailCache.current.set(dialog.order.id, detail.order);
                setDialog(current => ({ ...current, order: detail.order }));
            } catch (error) {
                setDialog(current => ({ ...current, error: 'El pedido fue actualizado, pero no se pudo recargar el detalle.' }));
            }
        }
        setQueueRefresh(value => value + 1);
        loadList(applied, list.page);
        const summary = result.summary || {};
        const parts = [
            `${summary.updated || 0} actualizados`,
            summary.alreadyApplied ? `${summary.alreadyApplied} ya entregados` : '',
            summary.expired ? `${summary.expired} fuera de periodo` : '',
            summary.invalidState ? `${summary.invalidState} con estado incompatible` : '',
            summary.emailFailures ? `${summary.emailFailures} correos fallidos` : ''
        ].filter(Boolean);
        setNotice(parts.join(' · '));
    }, [applied, dialog.order, list.page, loadList]);

    const columns = [
        { title: 'Pedido ONL', field: 'order_num' },
        { title: 'Pedido Odoo', field: 'odoo_order_name' },
        { title: 'Cliente', field: 'name' },
        { title: 'Fecha', field: 'order_date', render: row => formatAdminOrderTableDate(row.order_date) },
        { title: 'Total', field: 'total', render: row => formatAdminOrderTotal(row.total) },
        { title: 'Factura', field: 'req_invoice', render: row => row.req_invoice ? 'Sí' : 'No' },
        { title: 'Pago', field: 'payment_method' },
        { title: 'Envío', field: 'delivery_status' },
        { title: 'Paquetería', field: 'partner' },
        { title: 'Guía', field: 'tracking_guide' },
        { title: 'Artículos', field: 'item_count', render: row => (
            <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: '#DC7633' }}>
                {row.item_count}
            </Avatar>
        ) }
    ];

    return <>
        <Container maxWidth="xxxl" sx={{ mb: 5, mt: '90px' }}>
            <Typography variant="h4" fontWeight="light">Pedidos</Typography>
            <Queue partners={options.partners} refreshKey={queueRefresh}
                onTransition={transition} onBulkTransition={bulkTransition}
                onOpenDetail={openDetail} />
            <Card variant="outlined" sx={{ mt: 3 }}><CardContent>
                <Autocomplete options={searchOptions} loading={searching} filterOptions={value => value}
                    inputValue={searchText} onInputChange={(_event, value) => setSearchText(value)}
                    onChange={(_event, value) => value && openDetail(value.id)}
                    getOptionLabel={option => typeof option === 'string' ? option : `${option.order_num} · ${option.odoo_order_name}`}
                    renderOption={(props, option) => <li {...props} key={option.id}>
                        <Box><Typography variant="body2"><b>{option.order_num}</b> · {option.odoo_order_name}</Typography>
                            <Typography variant="caption">{option.name} · {formatAdminOrderDate(option.order_date)} · {option.delivery_status} · ${option.total.toFixed(2)}</Typography></Box>
                    </li>}
                    renderInput={params => <TextField {...params} label="Buscar pedido ONL u Odoo"
                        helperText="Escribe al menos dos caracteres" InputProps={{ ...params.InputProps,
                            endAdornment: <>{searching && <CircularProgress size={18} />}{params.InputProps.endAdornment}</> }} />} />
            </CardContent></Card>
            <Filters options={options} draft={draft} setDraft={setDraft} applied={applied}
                onApply={() => { setApplied({ ...draft }); loadList(draft, 1); }}
                onClear={() => { const clean = emptyFilters(options); setDraft(clean); setApplied(clean); loadList(clean, 1); }}
                exporting={exporting} onExport={async () => {
                    setExporting(true); try { await exportAdminOrders(applied); }
                    catch (error) { setNotice('No fue posible exportar los pedidos.'); }
                    finally { setExporting(false); }
                }} />
            <Box sx={{ mt: 6 }}>
                <Stack direction="row" flexWrap="wrap" sx={{ mb: 2, gap: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box className="register_odoo" />
                        <Typography variant="caption">Registrado en Odoo</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box className="no_register_odoo" />
                        <Typography variant="caption">No registrado en Odoo</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box className="btl_medical_order" />
                        <Typography variant="caption">Pedido con código médico BTL</Typography>
                    </Stack>
                </Stack>
                {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}
                <MaterialTable title="Detalles de pedidos" data={list.items} columns={columns}
                    isLoading={loading} onRowClick={(_event, row) => openDetail(row.id)}
                    options={{ search: false, filtering: false, paging: false, sorting: false,
                        headerStyle: { zIndex: 1 }, rowStyle: row => ({
                            backgroundColor: row.odoo_registered ? '#A9DFBF' : '',
                            borderLeft: row.is_btl ? '6px solid #7E57C2' : undefined
                        }) }}
                    localization={{ body: { emptyDataSourceMessage: 'No hay datos por mostrar' } }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                    <Typography variant="body2">{list.total === 0 ? '0' : `${(list.page - 1) * PAGE_SIZE + 1}–${Math.min(list.page * PAGE_SIZE, list.total)}`} de {list.total} pedidos</Typography>
                    <Pagination color="primary" count={list.totalPages} page={list.page}
                        onChange={(_event, page) => loadList(applied, page)} />
                </Stack>
            </Box>
        </Container>
        <DetailDialog open={dialog.open} loading={dialog.loading} error={dialog.error}
            order={dialog.order || adminOrder} partners={options.partners}
            onClose={() => setDialog(current => ({ ...current, open: false }))}
            onTransition={transition} />
        <Snackbar open={Boolean(notice)} autoHideDuration={7000} onClose={() => setNotice('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert severity="warning" onClose={() => setNotice('')}>{notice}</Alert>
        </Snackbar>
    </>;
};

export default AdminPedidos;
